/** Design controls call the panel actions; wrapper state comes from the scene. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
    ServiceDefinition,
    IShellService,
    SidePaneDefinition,
} from '@babylonjs/inspector';
import { ShellServiceIdentity } from '@babylonjs/inspector';
import type { TraceViewerElement } from 'trace-viewer';
import { documentRoot, getUndoManager } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';
import type { ToolContext } from './ai/agent-helpers';
import { catalogIndex, type IndexedAsset } from './ai/CatalogIndex';
import { onCatalogTokenChange } from './catalog-auth';
import { loadLibraryThumbnails, NO_LIBRARY_THUMBNAILS } from './design-thumbnails';
import {
    applyStyle,
    currentStage,
    KNOWN_DESIGN_SETS,
    listDesignStyles,
    listWrapperChoices,
    placeLibraryItem,
    removeWrapper,
    stageWrapper,
    swapDesignSet,
    type StageInfo,
} from './ai/design-actions';

type Tab = 'style' | 'form' | 'wrapper' | 'library';
const TABS: { id: Tab; label: string }[] = [
    { id: 'style', label: 'Style' },
    { id: 'form', label: 'Form' },
    { id: 'wrapper', label: 'Wrapper' },
    { id: 'library', label: 'Library' },
];

function getViewerElement(): TraceViewerElement | null {
    return document.querySelector('trace-viewer');
}

/** Lazily built, only when a control is actually clicked — importing
 * AgentService eagerly at module scope would pull its localStorage-backed
 * constructor into every consumer of this file (the MCP loader included). */
async function getToolContext(): Promise<ToolContext | null> {
    const { agentService } = await import('./ai/AgentService');
    const ctx = agentService.getToolContext('design-panel');
    if (!ctx.traceScene) return null;
    return ctx;
}

const tileBase: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: 84,
    padding: '6px 4px',
    border: '1px solid transparent',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.04)',
    color: 'inherit',
    font: 'inherit',
    fontSize: 11,
    cursor: 'pointer',
    textAlign: 'center',
};

function Tile(props: {
    label: string;
    active?: boolean;
    disabled?: boolean;
    swatch?: string[];
    thumbnailUrl?: string;
    onClick: () => void;
}): React.ReactElement {
    const { label, active, disabled, swatch, thumbnailUrl, onClick } = props;
    // A thumbnail that fails to load falls back like a missing one.
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const image = thumbnailUrl && thumbnailUrl !== failedUrl ? thumbnailUrl : null;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            style={{
                ...tileBase,
                borderColor: active ? '#5b9dff' : 'transparent',
                opacity: disabled ? 0.45 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
        >
            {image ? (
                <img
                    src={image}
                    alt=""
                    onError={() => setFailedUrl(image)}
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, background: '#222' }}
                />
            ) : swatch ? (
                <div style={{
                    width: 56, height: 56, borderRadius: 4, overflow: 'hidden',
                    display: 'grid', gridTemplateColumns: `repeat(${swatch.length}, 1fr)`,
                }}>
                    {swatch.map((c, i) => <div key={i} style={{ background: c }} />)}
                </div>
            ) : (
                <div style={{
                    width: 56, height: 56, borderRadius: 4, background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>
                    {label.charAt(0).toUpperCase()}
                </div>
            )}
            <span style={{
                width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{label}</span>
        </button>
    );
}

function Banner(props: { kind: 'error' | 'info'; children: React.ReactNode }): React.ReactElement {
    return (
        <div style={{
            padding: '6px 8px', borderRadius: 4, fontSize: 12, marginBottom: 8,
            background: props.kind === 'error' ? 'rgba(220,60,60,0.15)' : 'rgba(255,255,255,0.06)',
            color: props.kind === 'error' ? '#ff8080' : 'inherit',
        }}>{props.children}</div>
    );
}

export const DesignPanel: React.FC = () => {
    const [tab, setTab] = useState<Tab>('style');
    const [selected, setSelected] = useState<SceneObject | null>(null);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
    const [libraryQuery, setLibraryQuery] = useState('');
    const [libraryTick, setLibraryTick] = useState(0);
    const selectedRef = useRef<SceneObject | null>(null);
    useEffect(() => { selectedRef.current = selected; }, [selected]);

    useEffect(() => {
        const onViewportSelection = (event: Event): void => {
            const detail = (event as CustomEvent<{ object: SceneObject | null }>).detail;
            if (detail.object) {
                setSelected(detail.object);
            } else {
                setSelected(documentRoot(getViewerElement()?.traceScene));
            }
        };
        window.addEventListener('trace-viewer:selection-changed', onViewportSelection);
        // Seed from whatever is selected right now.
        const scene = getViewerElement()?.traceScene;
        setSelected(scene?.getSelectedObject() ?? documentRoot(scene) ?? null);
        return () => window.removeEventListener('trace-viewer:selection-changed', onViewportSelection);
    }, []);

    // Library index: catalogIndex owns its own load/refresh; poll its status
    // lightly so the panel reflects loading/ready/error without a bespoke
    // subscription (it has none today — same tradeoff catalog-tools.ts's
    // list_folders/search_catalog callers accept).
    useEffect(() => {
        if (tab !== 'library') return;
        catalogIndex.ensureLoaded().catch(() => { /* status below reflects the failure */ });
        const id = window.setInterval(() => setLibraryTick(t => t + 1), 500);
        return () => window.clearInterval(id);
    }, [tab]);

    // Style / wrapper / form tile thumbnails are CMS library content. Signed
    // out, the tiles keep their swatch and letter until sign-in brings them.
    const [thumbnails, setThumbnails] = useState(NO_LIBRARY_THUMBNAILS);
    useEffect(() => {
        let live = true;
        const load = (): void => {
            loadLibraryThumbnails()
                .then(loaded => { if (live) setThumbnails(loaded); })
                .catch(() => { /* the tiles keep their fallbacks */ });
        };
        load();
        const unsubscribe = onCatalogTokenChange(load);
        return () => { live = false; unsubscribe(); };
    }, []);

    const run = useCallback(async (label: string, action: (ctx: ToolContext, target: SceneObject) => Promise<{ ok: boolean; message: string }> | { ok: boolean; message: string }): Promise<void> => {
        const target = selectedRef.current;
        if (!target) { setStatus({ kind: 'error', text: 'Select an object first.' }); return; }
        setBusy(true);
        setStatus(null);
        try {
            const ctx = await getToolContext();
            if (!ctx) { setStatus({ kind: 'error', text: 'No scene loaded.' }); return; }
            const result = await action(ctx, target);
            setStatus({ kind: result.ok ? 'info' : 'error', text: result.message });
        } catch (err) {
            setStatus({ kind: 'error', text: `${label} failed: ${err instanceof Error ? err.message : String(err)}` });
        } finally {
            setBusy(false);
        }
    }, []);

    const [stage, setStage] = useState<StageInfo | null>(null);
    useEffect(() => {
        let live = true;
        let revision = 0;
        const load = (): void => {
            const request = ++revision;
            if (!selected) { setStage(null); return; }
            currentStage(selected)
                .then(value => { if (live && request === revision) setStage(value); })
                .catch(() => { if (live && request === revision) setStage(null); });
        };
        setStage(null);
        load();
        const unsubscribeUndo = getUndoManager().subscribe(load);
        const unsubscribeAuth = onCatalogTokenChange(load);
        return () => { live = false; unsubscribeUndo(); unsubscribeAuth(); };
    }, [selected]);
    const styles = useMemo(() => listDesignStyles(), []);
    const wrappers = useMemo(() => listWrapperChoices(), []);
    const libraryResults: IndexedAsset[] = useMemo(
        () => catalogIndex.isAvailable ? catalogIndex.search(libraryQuery, 40) : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- libraryTick is a poll signal, not a value read below
        [libraryQuery, libraryTick],
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 8, gap: 8, overflow: 'auto' }}>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
                {selected ? `Selected: ${selected.name}` : 'Nothing selected'}
            </div>

            <div role="tablist" aria-label="Design" style={{ display: 'flex', gap: 4 }}>
                {TABS.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        onClick={() => setTab(t.id)}
                        style={{
                            flex: 1, padding: '6px 4px', fontSize: 12, borderRadius: 4,
                            border: 'none', cursor: 'pointer',
                            background: tab === t.id ? 'rgba(91,157,255,0.25)' : 'rgba(255,255,255,0.05)',
                            color: 'inherit',
                        }}
                    >{t.label}</button>
                ))}
            </div>

            {status && <Banner kind={status.kind}>{status.text}</Banner>}
            {!selected && <Banner kind="info">Select an object in the scene to style, stage, or wrap it.</Banner>}

            {tab === 'style' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {styles.length === 0 && <Banner kind="info">No styles in the library.</Banner>}
                    {styles.map(s => (
                        <Tile
                            key={s.slug}
                            label={s.label}
                            disabled={busy || !selected}
                            thumbnailUrl={thumbnails.get('style', s.slug, [s.label])}
                            swatch={[
                                typeof s.values.backgroundColor === 'string' ? s.values.backgroundColor : '#333',
                                typeof s.values.accentColor === 'string' ? s.values.accentColor : '#888',
                            ]}
                            onClick={() => void run('Apply style', (_ctx, target) => applyStyle(target, s.slug))}
                        />
                    ))}
                </div>
            )}

            {tab === 'wrapper' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <Tile
                        label="None"
                        active={!stage}
                        disabled={busy || !selected || !stage}
                        onClick={() => void run('Remove wrapper', (ctx, target) => removeWrapper(ctx, target))}
                    />
                    {wrappers.map(w => (
                        <Tile
                            key={w.id}
                            label={w.label}
                            active={stage?.wrapperId === w.id}
                            disabled={busy || !selected}
                            thumbnailUrl={thumbnails.get('wrapper', w.id, [w.label, w.id.replace(/^display-/, '')])}
                            onClick={() => void run('Stage wrapper', (ctx, target) => stageWrapper(ctx, target, w.id))}
                        />
                    ))}
                </div>
            )}

            {tab === 'form' && (
                stage ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {KNOWN_DESIGN_SETS.map(set => (
                            <Tile
                                key={set}
                                label={set}
                                active={stage.set === set}
                                disabled={busy || !selected}
                                thumbnailUrl={thumbnails.get('form', set)}
                                onClick={() => void run('Swap form', (ctx, target) => swapDesignSet(ctx, target, set))}
                            />
                        ))}
                    </div>
                ) : (
                    <Banner kind="info">Stage a wrapper first (Wrapper tab) — Form swaps its design-set geometry.</Banner>
                )
            )}

            {tab === 'library' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                        value={libraryQuery}
                        onChange={e => setLibraryQuery(e.target.value)}
                        placeholder="Search your library…"
                        style={{ padding: '6px 8px', fontSize: 12, borderRadius: 4, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'inherit' }}
                    />
                    {catalogIndex.status === 'idle' && <Banner kind="info">Sign in to the catalog (top-right) to browse your library.</Banner>}
                    {catalogIndex.status === 'loading' && <Banner kind="info">Loading your library…</Banner>}
                    {catalogIndex.status === 'error' && <Banner kind="error">Could not load the library. Sign in to the catalog and retry.</Banner>}
                    {catalogIndex.status === 'ready' && libraryResults.length === 0 && (
                        <Banner kind="info">{libraryQuery ? `No matches for "${libraryQuery}".` : 'Your library is empty.'}</Banner>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflow: 'auto' }}>
                        {libraryResults.map(asset => (
                            <div key={asset.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4, borderRadius: 4, background: 'rgba(255,255,255,0.03)' }}>
                                {asset.thumbnailUrl ? (
                                    <img src={asset.thumbnailUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3, background: '#222' }} />
                                ) : (
                                    <div style={{ width: 32, height: 32, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }} />
                                )}
                                <div style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.title}</div>
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => void run('Place asset', async ctx => {
                                        const result = await placeLibraryItem({ assetId: asset.id }, ctx);
                                        return { ok: result.ok, message: result.message };
                                    })}
                                    style={{ fontSize: 11, padding: '4px 8px', borderRadius: 4, border: 'none', cursor: busy ? 'not-allowed' : 'pointer', background: 'rgba(91,157,255,0.35)', color: 'inherit' }}
                                >Place</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const DesignIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a7 7 0 1 0 3.5 13.06.75.75 0 0 0-.32-1.4c-.6.09-1.11-.42-.9-.98a1 1 0 0 1 .94-.65A2.5 2.5 0 0 0 14 8.5C14 4.36 11.31 1 8 1zM3.5 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm2-3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm2.5 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
    </svg>
);

export const DesignPanelServiceDefinition: ServiceDefinition<[], [IShellService]> = {
    friendlyName: 'Design Panel',
    consumes: [ShellServiceIdentity],

    factory: (shellService: IShellService) => {
        const paneDefinition: SidePaneDefinition = {
            key: 'design-panel',
            icon: DesignIcon,
            content: DesignPanel,
            horizontalLocation: 'right',
            verticalLocation: 'top',
            order: -1,
            title: 'Design',
        };
        const disposable = shellService.addSidePane(paneDefinition);
        return {
            dispose: () => disposable.dispose(),
        };
    },
};
